import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/** Magento footer stays mobile/accordion through 768px (not Horizon 750). */
const mediaQueryFooterBrandDesktop = matchMedia('(min-width: 769px)');

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #isFooterBrand() {
    return Boolean(this.closest('.footer-content--brand'));
  }

  /**
   * PDP product-details accordion (or any `.accordion[data-open-one-at-a-time]`)
   * opens only one row at a time.
   */
  get #openOneAtATime() {
    const group = this.closest('.accordion');
    if (!group) return false;

    return group.hasAttribute('data-open-one-at-a-time') || Boolean(this.closest('.product-details'));
  }

  /**
   * Horizon menus use 750px; Magento brand footer uses 769px so 768 stays accordion.
   * @returns {boolean}
   */
  #isMobileContext() {
    if (this.#isFooterBrand) {
      return !mediaQueryFooterBrandDesktop.matches;
    }

    return isMobileBreakpoint();
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    mediaQueryFooterBrandDesktop.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = this.#isMobileContext();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * When this row opens in an exclusive accordion group, close sibling rows.
   */
  #handleToggle = () => {
    if (!this.details.open || !this.#openOneAtATime) return;

    const group = this.closest('.accordion');
    if (!group) return;

    for (const sibling of group.querySelectorAll(':scope > accordion-custom')) {
      if (sibling === this) continue;

      const siblingDetails = sibling.querySelector('details');
      if (siblingDetails instanceof HTMLDetailsElement && siblingDetails.open) {
        siblingDetails.open = false;
      }
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = this.#isMobileContext();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
